import { useSelector, connect } from 'react-redux';
import {
  GroupedBalancesPerCategoryInfoModel,
  HomeDetailInfoModel,
  BillingInfoModel,
  GroupedBalancesInfoModel,
} from 'api/models.v2';
import cx from 'classnames';
import { useFastMaintenanceSettings } from 'hooks/useFastMaintenanceSettings';
import { selectSimulatePayments } from 'store/redux/accounts/selectors';
import {
  selectAccountPropertyByUnitId,
  selectAccountUnitByUnitId,
} from 'store/redux/homeowner-accounts/selectors';
import { formatString } from 'strings';
import { addressAndUnit } from 'utils/formatAddress';
import { BalanceItem } from './balance-item/balance-item';

type BalanceProps = GroupedBalancesPerCategoryInfoModel & {
  address?: string;
  homeId: HomeDetailInfoModel['homeId'];
  showAddress: boolean;
  isResident?: boolean;
  hideSplitIt?: boolean;
  noBorder?: boolean;
  handleFinancingModal: (
    id: string | number,
    balance: GroupedBalancesInfoModel,
    home: string,
  ) => void;
  billingInfo?: BillingInfoModel;
  handleShowBalanceDetailsModal?: (
    balance: GroupedBalancesInfoModel,
    home: string,
  ) => void;
};

function Balance({
  address: streetAddress,
  groupedBalancesInfo: baseItems,
  homeId,
  showAddress,
  billingInfo,
  noBorder = false,
  hideSplitIt = false,
  isResident = false,
  handleFinancingModal,
  handleShowBalanceDetailsModal,
}: BalanceProps) {
  const items = (baseItems ?? []).filter((item) => Boolean(item));

  const { isEnabled: isFastMaintenanceEnabled } = useFastMaintenanceSettings();
  const property = useSelector(selectAccountPropertyByUnitId(homeId));
  const unit = useSelector(selectAccountUnitByUnitId(homeId));

  const balanceItems = items.sort((a, b) => {
    if (a.sourceType !== 'Finance' && b.sourceType === 'Finance') {
      return -1;
    }

    if (a.sourceType === 'Finance' && b.sourceType !== 'Finance') {
      return 1;
    }

    return 0;
  });

  function navigateToFinanceDetails(id: any, balance: any, home: any) {
    if (handleFinancingModal) handleFinancingModal(id, balance, home);
  }

  if (!property && !isResident) {
    return null;
  }

  const date = new Date(new Date().toISOString());
  date.setDate(date.getDate() + 1);

  const address = !isResident
    ? formatString(
        addressAndUnit(property?.address, unit?.basicInfo?.unitNumber),
      )
    : streetAddress;

  return (
    <article>
      <main
        className={cx('transition-all ease-linear duration-hover', {
          'opacity-100 visible h-full': !showAddress,
        })}
      >
        <div
          className={
            noBorder ? '' : 'border-0 border-l-3 border-solid border-gray pl-xs'
          }
        >
          {balanceItems.map((item, itemIindex) => (
            <BalanceItem
              key={`balance-item-${homeId}-${itemIindex}`}
              balanceItem={item}
              navigateToFinanceDetails={navigateToFinanceDetails}
              handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
              address={address}
              isResident={isResident}
              hideSplitIt={hideSplitIt}
              noBorder={noBorder}
              billStatusInfo={item.billStatusInfo}
              billingInfo={billingInfo}
              isFastMaintenanceEnabled={isFastMaintenanceEnabled}
            />
          ))}
        </div>
      </main>
    </article>
  );
}

function mapStateProps(state: any) {
  return {
    simulation: selectSimulatePayments(state),
  };
}

export default connect(mapStateProps)(Balance);
